import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

import { Flex, FlexColumn } from 'src/components/flex';
import LeadSidebarExpandableItem from 'src/components/lead-sidebar/LeadSidebarExpandableItem';
import { useLeadListings } from 'src/services/api';
import type { ILead } from 'src/services/api';
import type { IListing } from 'src/services/api/listings/types';
import { getListingSecondaryText } from 'src/components/lead-sidebar/lead-sidebar-listings-utils';

import styles from 'src/components/lead-sidebar/LeadSidebarListings.module.css';

interface ListingRowProps {
    listing: IListing;
}

function ListingRow({ listing }: ListingRowProps) {
    const content = (
        <Flex alignItems="center" columnGap={1} className={styles.listingRow}>
            <Box className={styles.thumbnail}>
                {listing.image_url ? (
                    <Box
                        component="img"
                        src={listing.image_url}
                        alt=""
                        className={styles.thumbnailImage}
                    />
                ) : (
                    <HomeWorkOutlinedIcon fontSize="small" />
                )}
            </Box>
            <FlexColumn className={styles.listingText}>
                <Typography variant="body2" fontWeight="500" className="ellipsisText">
                    {listing.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" className="ellipsisText">
                    {getListingSecondaryText(listing)}
                </Typography>
            </FlexColumn>
            {listing.link && <OpenInNewOutlinedIcon fontSize="small" color="action" />}
        </Flex>
    );

    if (!listing.link) {
        return content;
    }

    return (
        <Link href={listing.link} target="_blank" rel="noreferrer" underline="none" color="inherit">
            {content}
        </Link>
    );
}

interface Props {
    lead: ILead;
}

export default function LeadSidebarListings({ lead }: Props) {
    const { data: listings, isLoading, isError } = useLeadListings(lead.uuid);
    const hasListingLinks = listings?.some((listing) => !!listing.link) ?? false;

    return (
        <LeadSidebarExpandableItem title="Listings" defaultExpanded>
            {hasListingLinks && (
                <Typography variant="body2" color="text.secondary" mb={1}>
                    Some listing links require you to be logged in to the provider website before opening.
                </Typography>
            )}
            {isLoading && (
                <Flex justifyContent="center" py={1}>
                    <CircularProgress size={18} />
                </Flex>
            )}
            {isError && (
                <Typography variant="body2" color="text.secondary">
                    Listings could not be loaded.
                </Typography>
            )}
            {!isLoading && !isError && listings?.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    No listings found.
                </Typography>
            )}
            {!isLoading && !isError && !!listings?.length && (
                <FlexColumn component="ul" rowGap={1} className={styles.list}>
                    {listings.map((listing) => (
                        <Box component="li" key={listing.id}>
                            <ListingRow listing={listing} />
                        </Box>
                    ))}
                </FlexColumn>
            )}
        </LeadSidebarExpandableItem>
    );
}
